import qs from 'querystring';
import i18n from 'i18n';
import moment from 'moment';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { get, isEmpty, assign, find, pick, set } from 'lodash';
import requester from 'utils/requester';
import getLocalized from 'utils/get-localized';
import api from 'config/api';
import apiCubic from 'config/api-cubic';
import { LIST_MAX_SIZE as ITEMS_PER_REQUEST } from 'config';
import urlHelper from 'helpers/urlhelper';
import { showToast } from 'actions/toast';
import { showModal, hideModal } from 'actions/modal';
import { showLoader, hideLoader } from 'actions/loader';
import { navigateToPlayerPage } from 'actions/player';
import { getIsLoggedInSelector, getContractNumberSelector } from 'reducers/profile/selectors';
import { getDetailsSelector, selectDetailsByContentId } from 'reducers/details/selectors';
import { CONTENT_TYPES } from 'constants/content-types';
import { MODAL_TYPES } from 'constants/modal-types';
import * as types from './types';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import Platform from 'platform';
import { INIT_UI_LANGUAGE } from 'constants/languages';

const CHECK_VOD_STATUS_TIMEOUT = 10000;
const CHECK_VOD_STATUS_MAX_ATTEMPS = 60;

const PURCHASE_ERROR_CODES_WITH_REPEAT = ['orderTvod_error_1'];

const CONTENT_TYPE_TO_URL = {
  [CONTENT_TYPES.VOD_ASSET]: 'asset',
  [CONTENT_TYPES.TV_SERIES]: 'tvSeries',
};

export const fetchEpisodeDetails = (episodeId, isLoggedIn) => {
  const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.details, isLoggedIn);

  return requester({
    method: 'get',
    url: `${api.host}${endpoint}asset/${episodeId}`,
    params: {
      vf: 'dash',
      detailed: true,
    },
  });
};

export const fetchDetails = (id, contentType, withSeasons) => async (dispatch, getState) => {
  const isLoggedIn = getIsLoggedInSelector(getState());
  const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.details, isLoggedIn);

  try {
    dispatch(showLoader());
    dispatch({
      type: types.FETCH,
      payload: { id },
    });

    const data = await requester({
      method: 'get',
      url: `${api.host}${endpoint}${contentType}/${id}`,
      params: {
        vf: 'dash',
        detailed: true,
      },
    });
    // they send wrong data structure here and it causes bugs
    // todo: api data validator with only necessary fields picking
    delete data.seasons;
    dispatch({ type: types.FETCH_SUCCESS, payload: { data, id } });
    if (data.id && withSeasons) await dispatch(fetchSeasons(data.id, withSeasons));
  } catch (error) {
    dispatch({ type: types.FETCH_ERROR, payload: { error, id } });
  } finally {
    dispatch(hideLoader());
  }
};

export const fetchSeasons = (id, withEpisodes) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.FETCH_SEASONS,
      payload: { id },
    });

    const isLoggedIn = getIsLoggedInSelector(getState());
    const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.seasons, isLoggedIn);

    dispatch({ type: types.FETCH_SEASONS, payload: { id } });

    const data = await requester({
      method: 'get',
      url: `${api.host}${endpoint}${id}`,
      params: { size: 50 },
    });

    dispatch({ type: types.FETCH_SEASONS_SUCCESS, payload: { id, data } });

    if (data && withEpisodes) {
      await dispatch(fetchEpisodesBatch({ seasons: data, contentId: id }));
    }
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_SEASONS_ERROR, payload: { id, error } });
  }
};

export const fetchEpisodes = (detailsUrl, contentId, seasonIndex) => async (dispatch, getState) => {
  const isLoggedIn = getIsLoggedInSelector(getState());
  const episodesEndpointUrl = urlHelper.getAuthorizedEndpointUrl(detailsUrl, isLoggedIn);

  try {
    dispatch({ type: types.FETCH_EPISODES, payload: { contentId } });

    const data = await requester({
      method: 'get',
      url: `${api.host}${episodesEndpointUrl}`,
      params: {
        size: 250,
        page: 0,
        sort: 'metadata.episodeNumber,asc',
        vf: 'dash',
        detailed: true,
      },
    });
    dispatch({ type: types.FETCH_EPISODES_SUCCESS, payload: { data, contentId, seasonIndex } });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_EPISODES_ERROR, payload: error });
  }
};

export const fetchEpisodesBatch =
  ({ seasons, contentId }) =>
  async (dispatch, getState) => {
    try {
      const isLoggedIn = getIsLoggedInSelector(getState());
      const params = { size: 250, page: 0, sort: 'title.title,asc', vf: 'dash', detailed: true };
      // copy object because "Array.sort" mutates object
      const promises = [...seasons]
        .sort((a, b) => a.seasonNumber - b.seasonNumber)
        .map((season) => {
          const url = `${api.host}${urlHelper.getAuthorizedEndpointUrl(season.episodeVodAssetsUrl, isLoggedIn)}`;
          return requester({ method: 'get', url, params });
        });
      const content = await Promise.all(promises);
      dispatch({
        type: types.FETCH_EPISODES_BATCH_SUCCESS,
        payload: { content, contentId },
      });
    } catch (error) {
      console.error(error);
    }
  };

export const fetchRelatedItems = (params) => async (dispatch) => {
  const { contentId, relatedContentUrls } = params;
  try {
    dispatch({ type: types.FETCH_RELATED_ITEMS, payload: { contentId } });

    const data = await Promise.all(
      relatedContentUrls.map(async (url) => {
        const id = parseInt(url.match(/\d+(?=\?)/g)[0]);
        const page = 0;
        const [link, query] = url.split('?');
        // Need to get the parameters from the original "url" and expand them with pagination
        const params = assign(qs.parse(query), { size: ITEMS_PER_REQUEST, page });

        const result = await requester({ method: 'get', url: `${api.host}${link}`, params });
        const data = pick(result, ['title', 'titles', 'content']);

        return assign(data, { id, url, page });
      }),
    );

    dispatch({ type: types.FETCH_RELATED_ITEMS_SUCCESS, payload: { data, contentId } });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_RELATED_ITEMS_ERROR, payload: { contentId, error } });
  }
};

export const fetchExtraRelatedItems = (params) => async (dispatch, getState) => {
  const { contentId, listId } = params;
  try {
    const related = get(getDetailsSelector(getState()), [contentId, 'related'], []);
    const currentList = find(related, { id: listId }) || {};
    const { url, isOnLazyLoad, isAllItemsWereLoaded } = currentList;

    if (isOnLazyLoad || isAllItemsWereLoaded) return;

    const page = currentList.page + 1;

    const [link, query] = url.split('?');
    // Need to get the parameters from the original "url" and expand them with pagination
    const params = assign(qs.parse(query), { size: ITEMS_PER_REQUEST, page });

    dispatch({ type: types.FETCH_EXTRA_RELATED_ITEMS, payload: { contentId, listId } });

    const { content } = await requester({ method: 'get', url: `${api.host}${link}`, params });

    dispatch({
      type: types.FETCH_EXTRA_RELATED_ITEMS_SUCCESS,
      payload: { isAllItemsWereLoaded: isEmpty(content), contentId, listId, content, page },
    });
  } catch (error) {
    console.error(error);
    dispatch({
      type: types.FETCH_EXTRA_RELATED_ITEMS_ERROR,
      payload: { contentId, listId, error },
    });
  }
};

export const fetchRecommendations = (params) => async (dispatch, getState) => {
  const { contentId, carouselId } = params;
  const isLoggedIn = getIsLoggedInSelector(getState());

  try {
    const page = 0;
    const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.list, isLoggedIn);
    const url = `${endpoint}${carouselId}`;

    dispatch({ type: types.FETCH_RECOMMENDATIONS, payload: { contentId } });

    const result = await requester({
      method: 'get',
      url: `${api.host}${url}`,
      params: {
        detailed: true,
        size: ITEMS_PER_REQUEST,
        page: page,
        getCarouselTitle: true,
      },
    });

    const data = pick(result, ['title', 'titles']);
    // Need to remove duplicate open object from recommendations
    // P.S. "contentId" could be string
    const content = result.content.filter(({ targetId }) => targetId !== Number(contentId));

    assign(data, { id: 'recommendations', content, page });

    dispatch({ type: types.FETCH_RECOMMENDATIONS_SUCCESS, payload: { data, contentId } });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_RECOMMENDATIONS_ERROR, payload: { contentId, error } });
  }
};

export const fetchExtraRecommendations = (params) => async (dispatch, getState) => {
  const { contentId, carouselId } = params;
  const isLoggedIn = getIsLoggedInSelector(getState());

  try {
    const recommendations = get(getDetailsSelector(getState()), [contentId, 'recommendations'], {});

    const { isOnLazyLoad, isAllItemsWereLoaded } = recommendations;

    if (isOnLazyLoad || isAllItemsWereLoaded) return;

    const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.list, isLoggedIn);
    const url = `${endpoint}${carouselId}`;
    const page = recommendations.page + 1;

    dispatch({ type: types.FETCH_EXTRA_RECOMMENDATIONS, payload: { contentId } });

    const { content } = await requester({
      method: 'get',
      url: `${api.host}${url}`,
      params: {
        detailed: true,
        size: ITEMS_PER_REQUEST,
        page: page,
        getCarouselTitle: true,
      },
    });

    dispatch({
      type: types.FETCH_EXTRA_RECOMMENDATIONS_SUCCESS,
      payload: { isAllItemsWereLoaded: isEmpty(content), contentId, content, page },
    });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_EXTRA_RECOMMENDATIONS_ERROR, payload: { contentId, error } });
  }
};

export const toggleVodObjectFavoriteStatus = (payload) => async (dispatch, getState) => {
  try {
    const { id } = payload;
    dispatch({ type: types.TOGGLE_WATCHLIST });
    const details = getDetailsSelector(getState());

    const isSeries = !!details[id].data.seasons;
    const assetType = isSeries ? CONTENT_TYPES.TV_SERIES : CONTENT_TYPES.VOD_ASSET;

    const isInFavorite = details[id].data.favorite;
    const apiEndPointByAssetType =
      assetType === CONTENT_TYPES.TV_SERIES ? api.endpoints.favoritesSeries : api.endpoints.favorites;

    const dataToPostByAssetType = assetType === CONTENT_TYPES.TV_SERIES ? { tvSeriesId: id } : { vodAssetId: id };

    await requester({
      method: isInFavorite ? 'delete' : 'post',
      url: isInFavorite ? `${api.host}${apiEndPointByAssetType}/${id}` : `${api.host}${apiEndPointByAssetType}`,
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      appendParams: true,
      data: dataToPostByAssetType,
    });

    const toastText = isInFavorite
      ? i18n.t('common.toast.videoFavoriteRemoved')
      : i18n.t('common.toast.videoFavoriteAdded');
    dispatch(showToast({ options: { toastText } }));
    dispatch({
      type: types.TOGGLE_WATCHLIST_SUCCESS,
      payload: { id, favorite: !isInFavorite },
    });
  } catch (error) {
    console.error(error);
    dispatch({
      type: types.TOGGLE_WATCHLIST_ERROR,
      payload: error,
    });
  }
};

export const buyDetailsItem = (payload) => async (dispatch, getState, history) => {
  try {
    const state = getState();
    const contractCode = getContractNumberSelector(state);
    const { assetId, assetName, tsuCode, assetType, typeTVOD } = payload;
    const authToken = apiCubic.getAuthToken();
    const data = {
      deviceType: Platform.getDeviceType(),
      language: INIT_UI_LANGUAGE,
      storePurchase: false,
      contractCode,
      tsuCode,
      typeTVOD,
      assetId,
      assetName,
      assetType,
    };

    dispatch({ type: types.BUY_DETAILS_ITEM, payload });

    dispatch(
      showModal(MODAL_TYPES.PENDING, {
        message: i18n.t('common.modal.purchasePendingModalMessage'),
        buttonText: i18n.t('common.close'),
        onModalBack: () => {
          dispatch(hideModal());
          history.goBack();
        },
      }),
    );

    innerAnalyticsVolia.postEvent({
      eventGroup: 'purchase TVOD',
      eventName: typeTVOD,
      eventValue: tsuCode,
      addInfo: `vod_asset_id=${assetId};channel_id=null;${
        assetType === 'TV_SERIES' ? 'content_type=null;type=TV_SERIES' : 'content_type=MOVIE;type=VOD_ASSET'
      };epgEventState=null`,
    });

    const result = await requester({
      method: 'post',
      url: `${apiCubic.host}${apiCubic.endpoints.orderTVOD}`,
      appendParams: false,
      extendHeadersWithSession: false,
      headers: { Authorization: `Basic ${authToken}` },
      data,
    });

    // Cubic API doesn't emit network error. So need to check error field in response
    if (result.error) throw result.error;

    const contentType = assetType === 'TV_SERIES' ? 'TV_SERIES' : 'VOD_ASSET';

    dispatch(checkVODPurchaseStatus({ contentId: assetId, contentType }));
    dispatch({ type: types.BUY_DETAILS_ITEM_SUCCESS, payload });
  } catch (error) {
    console.error(error);

    dispatch({ type: types.BUY_DETAILS_ITEM_ERROR, payload });
    // Not enough Money
    // Removed as for DG users we have separate flow and for other users we have QR page now
    /* if (error.code === 'orderTvod_error_1') {
      return repeat();
    }*/

    const { location } = history;
    const isUserStillOnTariffsPage = location.pathname?.includes('tariffs');
    const lastActiveElement = document.activeElement;

    const restoreFocus = () => {
      SpatialNavigationWrapper.focus('@');
      SpatialNavigationWrapper.focus(lastActiveElement);
    };
    // These are inexplicable requirements from Volia to be able to repeat the purchase for two error codes
    if (PURCHASE_ERROR_CODES_WITH_REPEAT.includes(error.code)) {
      dispatch(
        showModal(MODAL_TYPES.CONFIRM, {
          message: error.message,
          confirmText: i18n.t('common.repeat'),
          rejectText: i18n.t('common.close'),
          onModalConfirm: () => {
            dispatch(hideModal());
            dispatch(buyDetailsItem(payload));
          },
          onModalBack: () => {
            dispatch(hideModal());
            isUserStillOnTariffsPage ? history.goBack() : restoreFocus();
          },
        }),
      );
    } else {
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          message: error.message,
          buttonText: i18n.t('common.close'),
          onModalBack: () => {
            dispatch(hideModal());
            isUserStillOnTariffsPage ? history.goBack() : restoreFocus();
          },
        }),
      );
    }
  }
};

/**
 * Recursively checks the status of an object until it becomes available to watch.
 * This is necessary because the synchronization delay by the service
 * on the VOLIA side can be up to 5 minutes
 */
export const checkVODPurchaseStatus =
  (payload, attemptsLeft = CHECK_VOD_STATUS_MAX_ATTEMPS) =>
  async (dispatch, getState, history) => {
    try {
      if (attemptsLeft <= 0) return;

      const { contentId, contentType } = payload;
      const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.details, true);
      const { visibilityRights } = await requester({
        method: 'get',
        url: `${api.host}${endpoint}${CONTENT_TYPE_TO_URL[contentType]}/${contentId}`,
        params: {
          vf: 'dash',
          detailed: true,
        },
      });

      if (visibilityRights !== 'PLAY') {
        attemptsLeft--;
        setTimeout(() => dispatch(checkVODPurchaseStatus(payload, attemptsLeft)), CHECK_VOD_STATUS_TIMEOUT);
        return;
      }

      const { location } = history;
      const isUserStillOnTariffsPage = (location.pathname || '').includes('tariffs');
      // Should update whole item in store
      await dispatch(
        fetchDetails(contentId, CONTENT_TYPE_TO_URL[contentType], CONTENT_TYPES.TV_SERIES === contentType),
      );

      if (!isUserStillOnTariffsPage) return;

      // should select updated item from store
      const updatedDetails = selectDetailsByContentId(getState(), set({}, ['match', 'params', 'contentId'], contentId));

      const { visibilityVodType, endDate, title, titles } = updatedDetails.data;

      const message = i18n.t('common.modal.successPurchaseModalTitle');
      const description = i18n.t('common.modal.successPurchaseModalDescription', {
        vodTitle: getLocalized(titles, get(updatedDetails.data, 'titles.UA.title', title)),
        duration: (visibilityVodType === 'EST'
          ? i18n.t('common.forever')
          : i18n.t('common.for', { value: moment(endDate).locale(INIT_UI_LANGUAGE).fromNow(true) })
        ).toLowerCase(),
      });

      dispatch(
        showModal(MODAL_TYPES.CONFIRM, {
          message,
          description,
          confirmText: i18n.t('detailsVOD.watch'),
          rejectText: i18n.t('common.close'),
          onModalConfirm: () => {
            // if object is TV series - pass first episide to the player
            const firstEpisode = get(updatedDetails.data, ['seasons', 0, 'episodes', 'content', 0]);
            const item = firstEpisode || updatedDetails.data;
            dispatch(hideModal());
            dispatch(navigateToPlayerPage({ item, useReplace: true }));
          },
          onModalBack: () => {
            dispatch(hideModal());
            history.goBack();
          },
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

export const updateDetails = (payload) => ({ type: types.UPDATE_DETAILS, payload });

export const resetVideoProgress = (payload) => ({ type: types.RESET_VIDEO_PROGRESS, payload });

export const resetAllDetailsData = () => ({ type: types.RESET_ALL_DATA });

export const saveVideoProgress = (payload) => ({ type: types.SAVE_VIDEO_PROGRESS, payload });
